import {useNavigate} from 'react-router-dom';

import {APIError} from 'data/errors';
import {HttpStatusCode} from 'data/protocols/http';
import {TypeLocalNameEnum} from 'domain/usecases';
import {
	OperationStateTypesEnum,
	QueryParamsEnum,
	QueryParamsForTaggingEnum,
} from 'presentation/constants';
import {useContractStatus, usePersonalData} from 'presentation/hooks';
import {
	FlowEnum,
	REBOOKING_PATH,
	TELEMEDICINE_INFO,
} from 'presentation/pages/private/booking/constants';
import {useGetExamsDistrictsQuery} from 'presentation/pages/private/booking/queries/useGetExamsDistrictsQuery';

import {useQueryParams} from 'presentation/pages/private/hooks';

import {GetExamsDistrictsParams} from 'domain/usecases/private/booking-flow/exams/get-exams-districts';
import {GetHealthDistrictsParams} from 'domain/usecases/private/booking-flow/health/get-health-districts';
import {useMyAppointmentsWellBorn} from 'presentation/modules/myAppointments/hooks/useMyAppointmentsWellBorn';
import {useLocationStore} from 'presentation/pages/private/store/locationStore';
import {getUserInfoFromToken} from 'presentation/utils';

import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';

import {UseGoToRebookingParams, UseGoToRebookingReturn} from './types';
import {useUnitsListStore} from 'presentation/modules/booking/shared/store/useUnitsListStore';
import {getStepPosition} from 'presentation/modules/booking/shared/utils/getStepPosition';
import {useGetHealthDistrictsQuery} from 'presentation/modules/booking/health/ui/pages/HealthBooking/steps/HealthDistrictSelection/queries/useGetHealthDistrictsQuery';

export function useGoToRebooking(params: UseGoToRebookingParams): UseGoToRebookingReturn {
	const {
		specialtyName,
		specialtyCode,
		subSpecialtyCode,
		bookingNumber,
		bookingType,
		ufCode,
		cityCode,
		ufName,
		email,
		phone,
		flowType,
		isTelehealth,
		currentAppointmentLocation,
		locationType,
		setOperationState,
		setIsBornWellActiveState,
		setIsContractSuspendedModalActiveState,
		getBookingTypeAndOrigin,
		setActiveBookingFlowStep,
		isFavoriteDoctorsFlow,
		professionalCode,
		professionalName,
	} = params;

	const navigate = useNavigate();
	const setBookingData = useBookingStore(state => state.setBookingData);
	const {setSearchParams} = useQueryParams();
	const resetUnitResponseState = useUnitsListStore(state => state.resetUnitsList);
	const setLocation = useLocationStore(state => state.setLocation);
	const cdPessoaC = getUserInfoFromToken().cdPessoa;
	const {isContractSuspended} = useContractStatus();
	const {currentEmail, currentPhone} = usePersonalData();

	const isHealth = flowType === FlowEnum.HEALTH;
	const isExam = flowType === FlowEnum.EXAM;
	const isHealthOrExam = isHealth || isExam;

	const healthDistrictParams: GetHealthDistrictsParams = {
		stateCode: ufCode?.toUpperCase(),
		locationName: cityCode?.toUpperCase(),
		locationType: TypeLocalNameEnum.CIDADE,
		specialtyCode: Number(specialtyCode),
	};

	const examDistrictParams: GetExamsDistrictsParams = {
		cdUf: ufCode?.toUpperCase(),
		nmLocal: cityCode?.toUpperCase(),
		nmTipoLocal: TypeLocalNameEnum.CIDADE,
		cdTipoExame: specialtyCode,
	};

	const {refetch: refetchHealthDistricts} = useGetHealthDistrictsQuery({
		params: healthDistrictParams,
		enabled: false,
	});
	const {refetch: refetchExamsDistricts} = useGetExamsDistrictsQuery(examDistrictParams, false);
	const refetchDistricts = isHealth ? refetchHealthDistricts : refetchExamsDistricts;

	const {UNIT_STEP, DISTRICT_STEP, PROFESSIONAL_STEP} = getStepPosition({
		flowType,
		isManualLocation: true,
	});

	const {handleWellBornEligibility} = useMyAppointmentsWellBorn({
		specialtyName,
		specialtyCode,
		subSpecialtyCode: subSpecialtyCode || '',
		bookingNumber,
		bookingType,
		ufCode,
		cityCode,
		ufName,
		email,
		phone,
		flowType,
		isTelehealth,
		currentAppointmentLocation,
		locationType,
		setOperationState,
		setIsBornWellActiveState,
		getBookingTypeAndOrigin,
		setActiveBookingFlowStep,
	});

	const goToRebooking = async () => {
		setOperationState?.(OperationStateTypesEnum.SUBMITTING);

		if (isContractSuspended) {
			setIsContractSuspendedModalActiveState?.(true);
			setOperationState?.(OperationStateTypesEnum.DONE);
			return;
		}

		const wellBornHandled = await handleWellBornEligibility();
		if (wellBornHandled) {
			return;
		}

		let areDistrictsEmpty = false;
		if (isHealthOrExam) {
			const {
				data: districtList,
				error: districtListError,
				isError: isDistrictListError,
			} = await refetchDistricts();

			const districtListHasUnprocessableError =
				isDistrictListError &&
				districtListError instanceof APIError &&
				districtListError.statusCode === HttpStatusCode.unprocessable;
			const districtListEmpty = districtList && districtList?.length === 0;
			areDistrictsEmpty = Boolean(districtListHasUnprocessableError || districtListEmpty);
		}

		resetUnitResponseState();

		const getRebookingRedirectStep = () => {
			if (areDistrictsEmpty && isHealthOrExam || isFavoriteDoctorsFlow) {
				return UNIT_STEP;
			}

			if (isTelehealth) {
				return PROFESSIONAL_STEP;
			}

			return DISTRICT_STEP;
		};

		setActiveBookingFlowStep(getRebookingRedirectStep());

		const bookingOrigin = getBookingTypeAndOrigin();

		setBookingData(prev => ({
			...prev,
			specialtyCode,
			subSpecialtyCode: !subSpecialtyCode || subSpecialtyCode === '0' ? '' : subSpecialtyCode,
			specialtyName,
			bookingNumber,
			isRebooking: true,
			cityName: cityCode.toUpperCase(),
			ufCode,
			ufName: ufName.toUpperCase(),
			email: email || currentEmail,
			phone: phone || currentPhone,
			dateAt: '',
			hourAt: '',
			professionalCode: isFavoriteDoctorsFlow && professionalCode ? professionalCode : '',
			professionalName: isFavoriteDoctorsFlow && professionalName ? professionalName : '',
			unitAddress: '',
			unitCode: '',
			unitName: '',
			districtName: '',
			typeLocalName: TypeLocalNameEnum.CIDADE,
			bookingType,
			bookingOrigin,
			isScheduleScript: false,
			allDistrictsSelected: false,
			citiesNames: '',
			coordinates: '',
			isManualLocation: true,
			hasGeoDistrictsFilter: false,
			isFavoriteDoctorsFlow,
		}));

		setSearchParams(prev => {
			prev.delete(QueryParamsEnum.SEARCH_QUERY);
			return prev;
		});

		const baseParams: Record<string, string> = {
			[QueryParamsForTaggingEnum.STEP]: '1',
			[QueryParamsForTaggingEnum.SPECIALTY_CODE]: specialtyCode,
			[QueryParamsForTaggingEnum.SUB_SPECIALTY_CODE]:
				!subSpecialtyCode || subSpecialtyCode === '0' ? '' : subSpecialtyCode,
			[QueryParamsForTaggingEnum.SPECIALTY_NAME]: specialtyName,
			[QueryParamsForTaggingEnum.BOOKING_NUMBER]: bookingNumber,
			[QueryParamsForTaggingEnum.IS_REBOOKING]: 'true',
			[QueryParamsForTaggingEnum.CITY_NAME]: cityCode.trim().toUpperCase(),
			[QueryParamsForTaggingEnum.UF_CODE]: ufCode,
			[QueryParamsForTaggingEnum.UF_NAME]: ufName.trim().toUpperCase(),
			[QueryParamsForTaggingEnum.GEOLOCATION]: 'false',
		};

		if (isTelehealth) {
			setBookingData(prev => ({
				...prev,
				unitCode: TELEMEDICINE_INFO.code,
				unitName: TELEMEDICINE_INFO.name,
			}));

			baseParams[QueryParamsForTaggingEnum.UNIT_CODE] = TELEMEDICINE_INFO.code;
			baseParams[QueryParamsForTaggingEnum.UNIT_NAME] = TELEMEDICINE_INFO.name;
		}

		const taggingParams = Object.entries(baseParams)
			.map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
			.join('&');

		const path = REBOOKING_PATH[flowType as keyof typeof REBOOKING_PATH];
		const url = `${path}?${taggingParams}`;

		if (
			!isTelehealth &&
			(!currentAppointmentLocation.city || !currentAppointmentLocation.state?.cdUf)
		) {
			setLocation(cdPessoaC, locationType, {
				city: cityCode.toUpperCase(),
				state: {
					cdUf: ufCode,
					nmUf: ufName,
				},
				typeLocalName: TypeLocalNameEnum.CIDADE,
			});
		}

		setOperationState?.(OperationStateTypesEnum.DONE);
		navigate(url, {replace: true});
	};

	return {
		goToRebooking,
	};
}
